<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { brandPalette, brandStyle } from '../../brand'
import type { AccessIdentity } from './types'

// Access screens layout (login model of prompt-design-ui-ux.md, 07/10): grey page, one centered 360px card
// with the identity (Aubaine console, tenant brand or neutral), optional status icon, title and
// subtitle, then the screen content; the "Protegido pela Aubaine" seal sits under the card, only on the
// neutral identity (central address): an institution's screens are white label and never show it.

const props = withDefaults(
  defineProps<{
    identity: AccessIdentity
    title: string
    subtitle?: string | null
    status?: { icon: 'check' | 'alert' | 'lock' | 'spin'; tone: 'ok' | 'neutral' | 'error' } | null
    consoleLabel?: string
    sealLabel?: string | null
  }>(),
  { subtitle: null, status: null, consoleLabel: 'Console da plataforma', sealLabel: null },
)

const dark = ref(false)
let media: MediaQueryList | undefined
let themeObserver: MutationObserver | undefined
const syncDark = () => {
  const attr = document.documentElement.getAttribute('data-theme')
  dark.value = attr ? attr === 'dark' : !!media?.matches
}
// Each state change moves focus to the first thing to act on (as in the prototype), so keyboard
// and screen-reader users land on the new content.
const card = ref<HTMLElement | null>(null)
function focusFirst() {
  const target = card.value?.querySelector<HTMLElement>('input:not([readonly]):not([disabled]), button:not([disabled])')
  target?.focus({ preventScroll: true })
}
watch(
  () => props.title,
  () => void nextTick(focusFirst),
)

onMounted(() => {
  media = window.matchMedia?.('(prefers-color-scheme: dark)')
  media?.addEventListener?.('change', syncDark)
  themeObserver = new MutationObserver(syncDark)
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  syncDark()
  focusFirst()
})
onBeforeUnmount(() => {
  media?.removeEventListener?.('change', syncDark)
  themeObserver?.disconnect()
})

const style = computed(() =>
  brandStyle(brandPalette(props.identity.kind === 'tenant' ? props.identity.brandColor : null, dark.value)),
)
// The logo of the current theme; the dark one is optional and falls back to the normal logo.
const logoSrc = computed(() => (dark.value && props.identity.darkLogoUrl) || props.identity.logoUrl || null)
const logoFailed = ref(false)
watch(logoSrc, () => (logoFailed.value = false))
</script>

<template>
  <div class="access" :style="style">
    <main class="access__stage">
      <section ref="card" class="access__card" :class="{ 'access__card--center': !!status }" aria-labelledby="access-title">
        <div v-if="identity.kind === 'console'" class="access__brand access__brand--console">
          <span class="access__wordmark">Aubaine</span>
          <span class="access__console">{{ consoleLabel }}</span>
        </div>
        <div v-else class="access__brand">
          <img v-if="logoSrc && !logoFailed" :src="logoSrc" :alt="identity.name" class="access__logo" @error="logoFailed = true" />
          <span v-else class="access__name">{{ identity.name }}</span>
        </div>

        <span v-if="status" aria-hidden="true" class="access__status" :class="`access__status--${status.tone}`">
          <span v-if="status.icon === 'spin'" class="access__spinner"></span>
          <svg v-else-if="status.icon === 'check'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          <svg v-else-if="status.icon === 'lock'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" /></svg>
        </span>

        <div class="access__heading">
          <h1 id="access-title" class="access__title">{{ title }}</h1>
          <p v-if="subtitle" class="access__subtitle">{{ subtitle }}</p>
        </div>

        <slot />

        <div v-if="$slots.actions" class="access__actions"><slot name="actions" /></div>
        <div v-if="$slots.links" class="access__links"><slot name="links" /></div>
        <p v-if="$slots.footer" class="access__footer"><slot name="footer" /></p>
      </section>

      <span v-if="sealLabel && identity.kind === 'neutral'" class="access__seal">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
        {{ sealLabel }}
      </span>
    </main>
  </div>
</template>

<style scoped>
.access {
  min-height: 100vh;
  background: var(--surface-grouped);
  color: var(--label);
}
.access :deep(a) {
  color: var(--brand-link);
}
.access :deep(:focus-visible) {
  outline: 2px solid var(--brand-link, var(--accent));
  outline-offset: 2px;
}
.access__stage {
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  gap: 16px;
  padding: 32px 16px;
}
.access__card {
  width: 360px;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--surface-card);
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-list);
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.access__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.access__brand--console {
  gap: 2px;
}
.access__wordmark {
  font-size: var(--type-title-1-size);
  line-height: var(--type-title-1-line);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--type-title-1-tracking);
}
.access__console {
  font-size: var(--type-footnote-size);
  font-weight: var(--weight-medium);
  color: var(--secondary-label);
}
.access__logo {
  max-width: 200px;
  max-height: 48px;
  border-radius: var(--radius-lg);
  object-fit: contain;
}
.access__name {
  font-size: var(--type-headline-size);
  line-height: var(--type-headline-line);
  font-weight: var(--weight-semibold);
}
.access__status {
  align-self: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.access__status--ok {
  background: var(--tint-success-bg);
  color: var(--tint-success-fg);
}
.access__status--neutral {
  background: var(--tint-neutral-bg);
  color: var(--text-secondary);
}
.access__status--error {
  background: var(--tint-danger-bg);
  color: var(--tint-danger-fg);
}
.access__spinner {
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2.5px solid currentColor;
  border-top-color: transparent;
  animation: aub-spin 0.8s linear infinite;
}
.access__heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
}
.access__title {
  margin: 0;
  font-size: var(--type-title-1-size);
  line-height: var(--type-title-1-line);
  letter-spacing: var(--type-title-1-tracking);
  font-weight: var(--weight-medium);
  text-wrap: balance;
}
.access__subtitle {
  margin: 0;
  font-size: var(--type-body-size);
  line-height: var(--type-body-line);
  color: var(--text-secondary);
  text-wrap: pretty;
}
.access__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.access__links {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.access__footer {
  margin: 0;
  padding-top: 16px;
  border-top: var(--hairline) solid var(--separator-soft);
  text-align: center;
  font-size: var(--type-footnote-size);
  line-height: var(--type-footnote-line);
  color: var(--secondary-label);
  text-wrap: pretty;
}
.access__seal {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--type-caption-1-size);
  color: var(--secondary-label);
}
@media (max-width: 480px) {
  .access__stage {
    justify-content: flex-start;
    padding: 28px 16px;
  }
  .access__card {
    width: 100%;
    padding: 32px 20px;
  }
}
</style>
